/**
 * The one HTML-escaping primitive (issue #1261).
 *
 * This character table used to live in `lib/reports/export/markdownRenderer.ts`,
 * which is the wrong home for a shared primitive: that module imports `marked`,
 * so anything wanting to escape five characters had to pull a markdown engine
 * in with it. It now lives here and `markdownRenderer` re-exports it, so its
 * existing consumers (`htmlExporter`, `pagedHtml`) are unaffected.
 *
 * Escape ORDER matters: `&` must be replaced first, or the ampersand this
 * function introduces for `<` is itself escaped and `<` becomes `&amp;lt;`.
 * A single regex pass over a lookup table gets this right by construction —
 * do not rewrite it as chained `.replace()` calls.
 *
 * `lib/markdown/renderSafeMarkdownHtml.ts` (the shared markdown renderer, 2026-09-23)
 * uses it. The retired `components/ui/MarkdownMessage.tsx` and
 * `components/ui/MarkdownAnalysis.tsx` (unimported, awaiting deletion) kept private copies.
 * NOT YET CONSOLIDATED:
 * `components/knowledge-base/DocumentViewerDialog.tsx` and
 * `lib/server/accessControlInvitationEmail.ts` each still carry a private copy
 * of this table. They are out of scope for #1261 and are all correct today;
 * point them here when something next touches them.
 */

const HTML_ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

/**
 * Escape the five characters that change the meaning of HTML.
 *
 * `null` and `undefined` become the empty string rather than the words
 * "null"/"undefined", because callers interpolate nullable database columns.
 */
export function escapeHtml(value: unknown): string {
  return String(value ?? '').replace(/[&<>"']/g, (char) => HTML_ESCAPES[char]);
}
